import React, {forwardRef, useEffect, useImperativeHandle} from 'react';
import {bindActionCreators} from 'redux';
import {batch, connect} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {FmlxTooltip} from 'fmlx-common-ui';
import {isV2Instrument} from 'stores/business/instrumentStatusBusiness';

import {checkOngoingTipsPickupCalibration} from 'contents/HardwareSettings/TipsPickupCalibration/TipsPickupCalibrationBusiness';
import FastButton from '../../../components/FastButton';
import withIntl from '../../../lib/withIntl';
import '../SetupWizard.scss';
import {setWizardAllowedNext, setWizardFlag} from '../../../stores/actions/wizardStateAction';
import SetupWizardImage from '../SetupWizardImage';
import FormattedHTMLMessage from '../../../components/FormattedHTMLMessage';
import MotionControlPad from '../../PlateTypeEditor/MotionControlPad';
import {
  handleAlignA1,
  handleCheckTipPickup,
  handleEjectTray,
  handleMoveToIdlePosition,
  handleMoveVerticalCarrier,
  handleScanYawSequence,
  handleSetMarkerPositionAfterUpdateA1,
  handleUpdateA1Offset,
  yawSequenceErrorHandler,
} from '../../../stores/business/setupWizardBusiness/setupWizardInstrumentBusiness';
import {A1OffsetCalibrationSubstep, SetupWizardStep} from '../../../constants/SetupWizardEnum';
import SetupWizardGeneralStepPage from './SetupWizardGeneralStepPage';
import {handleIncrementCurrentWizardView} from '../../../stores/business/setupWizardBusiness/setupWizardNavigationBusiness';
import {initializeDefaultStepData} from '../../../stores/business/setupWizardBusiness/commonSetupWizardBusiness';
import {hideLoading, showLoading} from '../../../stores/actions/appStateAction';
import {CommonButtonLabel, FastRobotErrorcode} from '../../../constants/dataEnum';
import {saveState} from '../../../stores/business/stateBusiness';
import {showFormattedModal} from '../../../stores/business/commonModalBusiness';
import TipsPickupCalibrationSetupWizard from './FastV2/TipsPickupCalibrationSetupWizard';

const Container = styled.div`
  margin: 0 10vw;
  display: flex;
  flex-direction: column;
  gap: 1vh;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 7fr 4fr;
  gap: 20px;
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  border: 1.5px solid #b4b4b4;
  border-radius: 10px;
  padding-bottom: 2vh;
  text-align: left;
  padding-left: 1vw;
`;

const RightPanel = styled.div`
  border: 1.5px solid #b4b4b4;
  border-radius: 10px;
`;

function SubstepTwo({
  intl,
  onUpdateA1Offset,
  onSetMarkerPositionAfterUpdateA1,
  onResetProbe,
  onCheckTipPickUp,
  onSaveState,
  setAllowedNext,
  setStepProcessed,
  saveData,
  isUpdateA1OffsetAllowed,
}) {
  const handleCheckTipPickUp = async () => {
    const isNoError = await onCheckTipPickUp();
    saveData({isUpdateA1OffsetAllowed: isNoError, isSkippable: !isNoError});
    await onSaveState();
  };

  const handleResetProbe = async () => {
    await onResetProbe();
    saveData({isUpdateA1OffsetAllowed: false, isSkippable: true});
  };

  const handleUpdateOffset = async () => {
    const isUpdateA1Success = await onUpdateA1Offset();

    await onSetMarkerPositionAfterUpdateA1(async (isSetMarkerPositionSuccess) => {
      if (isSetMarkerPositionSuccess && isUpdateA1Success) {
        setStepProcessed();
        saveData({isUpdateA1OffsetAllowed: false, isA1AutomaticallyCalibrated: false});
        setAllowedNext(true);
      }

      await onSaveState();
    });
  };

  return (
    <Container className="substep-2">
      <ContentWrapper>
        <LeftPanel>
          <SetupWizardImage subStepIndex={1} stepName={SetupWizardStep.A1_OFFSET_CALIBRATION} />
          <div style={{paddingLeft: '2vw'}}>
            <FormattedHTMLMessage id="setupWizard.step.a1OffsetCalibration.substep2.leftPanel.text" />
          </div>
        </LeftPanel>
        <RightPanel>
          <div className="right-panel-top-part">
            <FastButton
              onClick={handleCheckTipPickUp}
              label={intl.formatMessage({
                id: 'setupWizard.step.a1OffsetCalibration.substep2.rightPanel.top.firstButton',
              })}
              type="positive"
              isResponsive
              buttonStyle={{minWidth: '10rem'}}
            />
            <p className="check-pick-tip-text">
              <span className="check-pick-tip-text-asterisk">*</span>
              {intl.formatMessage({id: 'setupWizard.step.a1OffsetCalibration.substep2.rightPanel.top.text'})}
            </p>
          </div>
          <div className="right-panel-bottom-part">
            <p className="motion-control-title">
              {intl.formatMessage({id: 'setupWizard.step.a1OffsetCalibration.substep2.rightPanel.bottom.title'})}
            </p>
            <div className="setup-wizard__motion-control-pad right-panel-bottom-part__MCP">
              <MotionControlPad
                onClickArrow={() => saveData({isUpdateA1OffsetAllowed: false})}
                moveValue={0.1}
                withExtendedTouchProbe={false}
              />
              <span className="setup-wizard__motion-control-pad__reset-button">
                <FastButton
                  isResponsive
                  variant="outline"
                  buttonStyle={{width: '7vw'}}
                  onClick={handleResetProbe}
                  label={intl.formatMessage({
                    id: 'setupWizard.step.a1OffsetCalibration.substep2.rightPanel.bottom.firstButton',
                  })}
                />
              </span>
            </div>
          </div>
        </RightPanel>
      </ContentWrapper>
      <div>
        <FmlxTooltip
          title={
            isUpdateA1OffsetAllowed
              ? ''
              : intl.formatMessage({
                  id: 'setupWizard.step.a1OffsetCalibration.substep2.leftPanel.updateA1Offset.tooltip',
                })
          }
          placement="top"
        >
          <span>
            <FastButton
              disabled={!isUpdateA1OffsetAllowed}
              onClick={handleUpdateOffset}
              label={intl.formatMessage({
                id: 'setupWizard.step.a1OffsetCalibration.substep2.leftPanel.updateA1Offset.firstButton',
              })}
              buttonStyle={{width: '15vw'}}
              isResponsive
            />
          </span>
        </FmlxTooltip>
      </div>
    </Container>
  );
}

SubstepTwo.propTypes = {
  onUpdateA1Offset: PropTypes.func.isRequired,
  onSetMarkerPositionAfterUpdateA1: PropTypes.func.isRequired,
  onResetProbe: PropTypes.func.isRequired,
  onCheckTipPickUp: PropTypes.func.isRequired,
  onSaveState: PropTypes.func.isRequired,
  setAllowedNext: PropTypes.func.isRequired,
  setStepProcessed: PropTypes.func.isRequired,
  saveData: PropTypes.func.isRequired,
  isUpdateA1OffsetAllowed: PropTypes.bool.isRequired,
  intl: PropTypes.any.isRequired,
};
// #region step four container

const mapStateToProps = (state) => ({
  wizardViewDataState: state.wizardState.wizardViewData,
});

const mapDispatchToProps = (dispatch) => ({
  appStateActionShowLoading: bindActionCreators(showLoading, dispatch),
  appStateActionHideLoading: bindActionCreators(hideLoading, dispatch),
  stateBusinessSaveState: bindActionCreators(saveState, dispatch),
  wizardStateActionSetAllowedNext: bindActionCreators(setWizardAllowedNext, dispatch),
  wizardStateActionSetWizardFlag: bindActionCreators(setWizardFlag, dispatch),
  instrumentBusinessUpdateA1Offset: bindActionCreators(handleUpdateA1Offset, dispatch),
  instrumentBusinessResetProbePosition: bindActionCreators(handleAlignA1, dispatch),
  instrumentBusinessCheckTipPickUp: bindActionCreators(handleCheckTipPickup, dispatch),
  commonModalBusinessShowFormattedModal: bindActionCreators(showFormattedModal, dispatch),
  setupWizardNavBusinessHandleIncrementCurrentWizardView: bindActionCreators(
    handleIncrementCurrentWizardView,
    dispatch,
  ),
  SWInstrumentYawSequenceErrorHandler: bindActionCreators(yawSequenceErrorHandler, dispatch),
  SWInstrumentBusinessHandleScanYawSequence: bindActionCreators(handleScanYawSequence, dispatch),
  SWInstrumentBusinessHandleEjectTray: bindActionCreators(handleEjectTray, dispatch),
  SWInstrumentBusinessHandleMoveVerticalCarrier: bindActionCreators(handleMoveVerticalCarrier, dispatch),
  SWInstrumentBusinessHandleMoveToIdlePosition: bindActionCreators(handleMoveToIdlePosition, dispatch),
  SWInstrumentBusinessHandleSetMarkerPositionAfterUpdateA1: bindActionCreators(
    handleSetMarkerPositionAfterUpdateA1,
    dispatch,
  ),
  checkTipsPickupCalibrationOngoing: bindActionCreators(checkOngoingTipsPickupCalibration, dispatch),
  commonSWBusinessInitializeDefaultStepData: bindActionCreators(initializeDefaultStepData, dispatch),
});

const StepFourSetupWizard = forwardRef(
  (
    {
      intl,
      stateBusinessSaveState,
      instrumentBusinessUpdateA1Offset,
      instrumentBusinessResetProbePosition,
      instrumentBusinessCheckTipPickUp,
      onSaveStepData,
      commonSWBusinessInitializeDefaultStepData,
      commonModalBusinessShowFormattedModal,
      setupWizardNavBusinessHandleIncrementCurrentWizardView,
      SWInstrumentYawSequenceErrorHandler,
      SWInstrumentBusinessHandleEjectTray,
      SWInstrumentBusinessHandleMoveVerticalCarrier,
      SWInstrumentBusinessHandleMoveToIdlePosition,
      SWInstrumentBusinessHandleScanYawSequence,
      SWInstrumentBusinessHandleSetMarkerPositionAfterUpdateA1,
      checkTipsPickupCalibrationOngoing,
      wizardStateActionSetAllowedNext,
      wizardStateActionSetWizardFlag,
      stepData,
    },
    ref,
  ) => {
    const {
      currentSubstep,
      isUpdateA1OffsetAllowed,
      isStepProcessed,
      isA1AutomaticallyCalibrated,
      hasYawSequenceScanned,
      isSkippable,
      scanYawErrorCode,
    } = stepData || {};

    const showModalA1hasBeenCalibrated = () => {
      const successCallback = async () => {
        await SWInstrumentBusinessHandleEjectTray();
        onSaveStepData({isStepProcessed: true});
        setupWizardNavBusinessHandleIncrementCurrentWizardView(true, true);
      };

      commonModalBusinessShowFormattedModal(
        'modal.setupWizard.a1OffsetCalibrated',
        [{onClick: successCallback}, {label: CommonButtonLabel.CLOSE}],
        {hideClose: true},
      );
    };

    const showEmptyMarkerDataModal = () => {
      commonModalBusinessShowFormattedModal('modal.setupWizard.emptyMarkerData', [() => {}]);
    };

    // checking yaw sequence to determined if A1 offset is calibrated or not
    const checkYawSequence = async () => {
      const {errorCode, isA1Calibrated} = await SWInstrumentBusinessHandleScanYawSequence(checkYawSequence);
      const hasYawSeqScanned =
        errorCode === FastRobotErrorcode.NoError || errorCode === FastRobotErrorcode.TRAY_ID_POSITION_NOT_FOUND;

      onSaveStepData({
        hasYawSequenceScanned: hasYawSeqScanned,
        isA1AutomaticallyCalibrated: isA1Calibrated,
        isSkippable: isA1Calibrated,
        scanYawErrorCode: errorCode,
      });

      checkA1OffsetStatus(errorCode, isA1Calibrated, true);
    };

    const checkA1OffsetStatus = async (scanYawErrorCodeParam, isA1CalibratedParam, skipYawChecking) => {
      const errorCode = scanYawErrorCodeParam || scanYawErrorCode;
      const isA1AutoCalibrated = isA1CalibratedParam || isA1AutomaticallyCalibrated;

      // If yaw sequence hasn't been scanned yet
      if (!hasYawSequenceScanned && !skipYawChecking) {
        await checkYawSequence();
        return;
      }
      // check has A1 been calibrated
      if (isA1AutoCalibrated) {
        if (isV2Instrument()) return;

        showModalA1hasBeenCalibrated();
        return;
      }

      // check if the marker data position exists
      if (errorCode === FastRobotErrorcode.NoError) {
        if (!isA1AutoCalibrated && !isStepProcessed && !isV2Instrument()) {
          showEmptyMarkerDataModal();
        }
        return;
      }

      // check for errors that occurs while scanning yaw sequence.
      // if being called from checkYawSequence func, the error handler wont be recalled
      // because the errors are already being handled inside the SWInstrumentBusinessHandleScanYawSequence func
      if (!skipYawChecking && errorCode) {
        SWInstrumentYawSequenceErrorHandler(errorCode, checkYawSequence);
      }
    };

    const setStepProcessed = () => {
      onSaveStepData({
        isSkippable: true,
        isStepProcessed: true,
        hasYawSequenceScanned: true,
        scanYawErrorCode:
          scanYawErrorCode === FastRobotErrorcode.TRAY_ID_POSITION_NOT_FOUND
            ? scanYawErrorCode
            : FastRobotErrorcode.NoError,
      });
    };

    const onContinue = async (skipStep) => {
      let isNoError;
      let isCheckContinueButtonAccess = true;

      const isProceedToCheckTipPickup =
        currentSubstep === A1OffsetCalibrationSubstep.GUIDE_IMAGE && !skipStep && !isV2Instrument();

      if (isProceedToCheckTipPickup) {
        isNoError = await instrumentBusinessResetProbePosition();
        if (!isStepProcessed) {
          isCheckContinueButtonAccess = false;
          wizardStateActionSetAllowedNext(false);
        }
      } else {
        isNoError = await SWInstrumentBusinessHandleMoveVerticalCarrier();
        isNoError = isNoError ? await SWInstrumentBusinessHandleMoveToIdlePosition() : false;

        if (isA1AutomaticallyCalibrated || !skipStep) {
          batch(() => {
            setStepProcessed();
            onSaveStepData({
              scanYawErrorCode:
                scanYawErrorCode !== FastRobotErrorcode.TRAY_ID_POSITION_NOT_FOUND ? null : scanYawErrorCode,
            });
          });
        }
      }

      return {isChangeStep: isNoError, isCheckContinueButtonAccess};
    };

    const onBack = async () => {
      if (currentSubstep === A1OffsetCalibrationSubstep.CHECK_TIP_PICK_UP) {
        await SWInstrumentBusinessHandleEjectTray();
      }
    };

    useImperativeHandle(ref, () => ({
      onContinue,
      onBack,
    }));

    useEffect(() => {
      if (currentSubstep === A1OffsetCalibrationSubstep.GUIDE_IMAGE) {
        commonSWBusinessInitializeDefaultStepData();
        checkA1OffsetStatus();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSubstep]);

    useEffect(() => {
      if (!isSkippable) return;

      if (
        (!isA1AutomaticallyCalibrated && !isStepProcessed) ||
        currentSubstep === A1OffsetCalibrationSubstep.CHECK_TIP_PICK_UP
      ) {
        onSaveStepData({isSkippable: false});
      }
    }, [isSkippable, currentSubstep, isA1AutomaticallyCalibrated, isStepProcessed, onSaveStepData]);

    useEffect(() => {
      checkTipsPickupCalibrationOngoing();
    }, [checkTipsPickupCalibrationOngoing]);

    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {isV2Instrument() ? (
          <TipsPickupCalibrationSetupWizard />
        ) : (
          <>
            {currentSubstep === A1OffsetCalibrationSubstep.GUIDE_IMAGE && (
              <SetupWizardGeneralStepPage showInfo={!isA1AutomaticallyCalibrated} withImageCaption={false} />
            )}
            {currentSubstep === A1OffsetCalibrationSubstep.CHECK_TIP_PICK_UP && (
              <div className="step-a1-offset-calibration-substep-2 flex-column">
                <SubstepTwo
                  intl={intl}
                  onCheckTipPickUp={instrumentBusinessCheckTipPickUp}
                  onUpdateA1Offset={instrumentBusinessUpdateA1Offset}
                  onSetMarkerPositionAfterUpdateA1={SWInstrumentBusinessHandleSetMarkerPositionAfterUpdateA1}
                  onResetProbe={instrumentBusinessResetProbePosition}
                  saveData={onSaveStepData}
                  setAllowedNext={wizardStateActionSetAllowedNext}
                  onSetWizardFlag={wizardStateActionSetWizardFlag}
                  isUpdateA1OffsetAllowed={isUpdateA1OffsetAllowed}
                  onSaveState={stateBusinessSaveState}
                  setStepProcessed={setStepProcessed}
                />
              </div>
            )}
          </>
        )}
      </>
    );
  },
);

StepFourSetupWizard.propTypes = {
  intl: PropTypes.any.isRequired,
  stateBusinessSaveState: PropTypes.func.isRequired,
  instrumentBusinessUpdateA1Offset: PropTypes.func.isRequired,
  instrumentBusinessResetProbePosition: PropTypes.func.isRequired,
  instrumentBusinessCheckTipPickUp: PropTypes.func.isRequired,
  onSaveStepData: PropTypes.func.isRequired,
  commonSWBusinessInitializeDefaultStepData: PropTypes.func.isRequired,
  commonModalBusinessShowFormattedModal: PropTypes.func.isRequired,
  setupWizardNavBusinessHandleIncrementCurrentWizardView: PropTypes.func.isRequired,
  SWInstrumentYawSequenceErrorHandler: PropTypes.func.isRequired,
  SWInstrumentBusinessHandleEjectTray: PropTypes.func.isRequired,
  SWInstrumentBusinessHandleMoveVerticalCarrier: PropTypes.func.isRequired,
  SWInstrumentBusinessHandleMoveToIdlePosition: PropTypes.func.isRequired,
  SWInstrumentBusinessHandleScanYawSequence: PropTypes.func.isRequired,
  SWInstrumentBusinessHandleSetMarkerPositionAfterUpdateA1: PropTypes.func.isRequired,
  checkTipsPickupCalibrationOngoing: PropTypes.func.isRequired,
  wizardStateActionSetAllowedNext: PropTypes.func.isRequired,
  wizardStateActionSetWizardFlag: PropTypes.func.isRequired,
  stepData: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(withIntl(StepFourSetupWizard));
